import React, { useState } from "react";

import { Code, InfoSmIcon as InfoSm, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { CLI_COMMAND_NAME } from "Constants/documentationUrls";
import { Button, IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import getUrl from "Libs/getUrl";
import { capitalize, goToEnvironment } from "Libs/utils";
import { merge } from "Reducers/environment/actions/merge";
import { useAppDispatch } from "Store/hooks";

import * as S from "./MergePane.styles";
import MergeRadioDialog from "./MergeRadioDialog";

import type { Environment } from "@packages/client";

const radioOptions = ["child", "default", "minimum", "manual"];

type MergeContentSizingProps = {
  parent?: Environment;
  environment?: Environment;
  organizationId: string;
  projectId: string;
  environmentId: string;
  isLoading?: boolean;
  cliDialog: React.ReactNode;
};

const MergeContentSizing = ({
  parent,
  environment,
  organizationId,
  projectId,
  environmentId,
  isLoading,
  cliDialog
}: MergeContentSizingProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [radioSelection, setRadioSelection] = useState(() => radioOptions[0]);

  const command = intl.formatMessage(
    { id: "merge.command" },
    {
      commandName: CLI_COMMAND_NAME,
      action:
        radioSelection === "child" ? "" : ` --resources-init=${radioSelection}`
    }
  );

  const resourceInitializationDialog = (
    <Dialog
      align="end"
      title={intl.formatMessage({
        id: "modal.resource.initialization.dialog.title",
        defaultMessage: "Resource initialization strategy"
      })}
      description={intl.formatMessage({
        id: "modal.resource.initialization.dialog.description",
        defaultMessage:
          "You can define which strategy to use when allocating resources for any new app or service containers."
      })}
      analyticId="modal.resource.initialization.dialog.description"
      link={
        <Button
          analyticId="learnmore"
          href={intl.formatMessage({
            id: "links.manage.resources.init.define.strategy"
          })}
          variant="link"
        >
          {intl.formatMessage({ id: "learnmore" })}
        </Button>
      }
      trigger={
        <IconControl>
          <InfoSm />
        </IconControl>
      }
    />
  );

  const handleMerge = async () => {
    const response = await dispatch(
      merge({
        organizationId,
        projectId,
        environmentId,
        body: {
          resources: {
            init: radioSelection
          }
        }
      })
    );

    if (response?.payload?.parameters) {
      const environmentOverviewUrl = getUrl({
        key: "organization.project.environment",
        props: {
          organizationId,
          projectId,
          environmentId
        }
      });
      navigate(environmentOverviewUrl);
    }
  };

  return (
    <>
      <Typography.BodyText size="medium" weight="regular">
        <S.DescriptionWrapper>
          <div>
            {intl.formatMessage(
              { id: "merge.question.sizing_api_enabled" },
              {
                parentEnvironment: (
                  <S.SimpleBodyText size="medium" weight="semiBold">
                    {parent?.title}
                  </S.SimpleBodyText>
                ),
                currentEnvironment: (
                  <S.SimpleBodyText size="medium" weight="semiBold">
                    {environment?.title}
                  </S.SimpleBodyText>
                )
              }
            )}
          </div>
        </S.DescriptionWrapper>
      </Typography.BodyText>

      <S.DialogWrapperSizing>
        <S.ResourceBodyText>
          {intl.formatMessage(
            {
              id: "merge.radioGroup.description"
            },
            {
              parentEnvironment: (
                <S.SimpleBodyText size="medium" weight="semiBold">
                  {parent?.title}
                </S.SimpleBodyText>
              )
            }
          )}
        </S.ResourceBodyText>
        {resourceInitializationDialog}
      </S.DialogWrapperSizing>
      <S.RadioGroup
        defaultValue={radioSelection}
        onValueChange={value => {
          setRadioSelection(value);
        }}
      >
        {radioOptions.map(value => {
          return (
            <S.RadioInput
              key={`merge-modal${value}`}
              value={value}
              id={value}
              label={
                <S.DialogWrapperSizing>
                  {intl.formatMessage({
                    id: `merge.radioBtn.${value}`
                  })}
                  <MergeRadioDialog radioOption={value} />
                </S.DialogWrapperSizing>
              }
            />
          );
        })}
      </S.RadioGroup>

      <S.Separator />
      <S.CliDialogWrapper>
        <Typography.Label textStyle="default">
          {intl.formatMessage({
            id: "merge.cli.sizing_api_enabled"
          })}
        </Typography.Label>
        {cliDialog}
      </S.CliDialogWrapper>
      <Code.Block isCopyable singleLine id={command}>
        {command}
      </Code.Block>

      {isLoading ? (
        <S.LoadingSuccess />
      ) : (
        <S.InnerButtonWrapperSizing>
          <Button
            analyticId="cancel"
            id="environment-merge-cancel-btn"
            variant="secondary"
            onClick={() => {
              goToEnvironment(
                navigate,
                organizationId,
                projectId,
                environmentId
              );
            }}
          >
            {capitalize(intl.formatMessage({ id: "cancel" }))}
          </Button>
          <Button
            id="environment-merge-save-btn"
            type="submit"
            onClick={handleMerge}
            analyticId="merge.button"
          >
            {capitalize(intl.formatMessage({ id: "merge.button" }))}
          </Button>
        </S.InnerButtonWrapperSizing>
      )}
    </>
  );
};
export default MergeContentSizing;
